import { getItemDef } from "../items";
import { getNetworks } from "../networks";
import {
  addCargoToNetwork,
  effectiveCells,
  networkCargoCapacity,
  networkCargoMass,
  shipCellSize,
  shipExtent,
} from "../ship";
import { coolSpark, emitSparks, sparkHeatCapJPerK, worldNormalToShipFace } from "../sparks";
import { AMBIENT_K, T4_REF_RANGE_K4, collisionHeatJ, heatShareJ, radiativeCool } from "../thermal";
import { applyBlastDamage, damageModule } from "../tick";
import type {
  Projectile,
  Ship,
  ShipModule,
  SpaceItem,
  Spark,
  World,
} from "../types";
import { isFar } from "../lod";
import type { SimViewport } from "../tick";
import { getCollisionShape } from "./collision-shapes";
import { jitterAngle } from "./config";
import { narrowPhase } from "./contact";
import { SpatialGrid } from "./grid";
import { kineticHP, resolveContact } from "./resolve";
import { buildShipCollider } from "./sdf";
import type { BodyHandle, Contact, SubCollider } from "./types";

const SHIP_RESTITUTION = 0.02;
const ITEM_RESTITUTION = 0.2;
const PROJ_RESTITUTION = 0.05;
const SPARK_RESTITUTION = 0.35;

/** Yield thresholds (J of contact KE = ½·μ·v_closing²). Restitution ramps
 *  linearly to 0 between E_YIELD_J and E_FULL_PLASTIC_J. */
const E_YIELD_J = 20;
const E_FULL_PLASTIC_J = 2000;

const SHIP_TOUGHNESS = 1;
const ITEM_TOUGHNESS = 1;
const PROJ_TOUGHNESS = 1;
/** Sparks deposit only heat. */
const SPARK_TOUGHNESS = 0;

const SPARK_RADIUS_M = 0.25;

const MAX_SPARKS = 4096;

// Below this speed a "ricochet" is treated as embed → detonate warhead.
const ABSORB_SPEED_THRESHOLD_M_S = 50;
const RICOCHET_HEAT_JITTER = 0.5;
const RICOCHET_GLOW_K = 4000;
const SOFT_ABSORB_HALF_ANGLE_RAD = Math.PI / 2;
const STEFAN_BOLTZMANN = 5.670374419e-8;

function projectileRadiateCoefW(p: Projectile): number {
  const A = 4 * Math.PI * p.thermalRadiusM * p.thermalRadiusM;
  return p.emissivity * STEFAN_BOLTZMANN * A * T4_REF_RANGE_K4;
}

const _handles: BodyHandle[] = [];
const _handlePool: BodyHandle[] = [];
const _subPool: SubCollider[] = [];
const _pairsA: BodyHandle[] = [];
const _pairsB: BodyHandle[] = [];
const _pairsSeen = new Set<number>();
const _contacts: Contact[] = [];
const _grid = new SpatialGrid();

function makeHandleShell(): BodyHandle {
  return {
    id: 0,
    idx: 0,
    kind: "rigid",
    group: "ship",
    ref: null,
    pos: { x: 0, y: 0 },
    prevPos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
    rot: 0,
    prevRot: 0,
    angVel: 0,
    mass: 0,
    inertia: 0,
    restitution: 0,
    toughness: 0,
    tempK: 0,
    heatCap: 0,
    pointRadius: 0,
    subs: [],
    radius: 0,
    owner: -1,
    connected: null,
    noSelfContact: false,
    toi: 1,
    dead: false,
  };
}

function acquireHandle(): BodyHandle {
  return _handlePool.pop() ?? makeHandleShell();
}

function releaseHandle(h: BodyHandle): void {
  for (let i = 0; i < h.subs.length; i++) {
    const s = h.subs[i];
    s.ref = null;
    _subPool.push(s);
  }
  h.subs.length = 0;
  h.ref = null;
  h.connected = null;
  _handlePool.push(h);
}

function acquireSub(): SubCollider {
  const s = _subPool.pop();
  if (s) return s;
  return { hw: 0, hh: 0, cx: 0, cy: 0, rot: 0, alive: true, ref: null };
}

/** Caller must have applied all forces to body.vel/angVel. Beams aren't bodies.
 *  When `vp` is given, only bodies inside the active zone (ACTIVE_RADIUS_M of
 *  vp.focus) get collision handles; far bodies are coasted by the caller. */
export function physicsStep(world: World, dt: number, dirty: Set<Ship>, vp?: SimViewport): void {
  for (let i = 0; i < _handles.length; i++) releaseHandle(_handles[i]);
  _handles.length = 0;

  collectShipHandles(world, vp);
  collectItemHandles(world, vp);
  collectProjectileHandles(world);
  collectSparkHandles(world);

  // Sparks integrate at the full tick rate, in lockstep with every other body,
  // so a co-moving spark never desyncs from its reference frame. (Half-rate
  // 2× dt split co-moving sparks into ~v·dt-offset clusters at high speed.)

  for (let i = 0; i < _handles.length; i++) {
    const h = _handles[i];
    h.idx = i;
    h.prevPos.x = h.pos.x;
    h.prevPos.y = h.pos.y;
    h.pos.x += h.vel.x * dt;
    h.pos.y += h.vel.y * dt;
    if (h.kind === "rigid") {
      h.prevRot = h.rot;
      h.rot += h.angVel * dt;
    }
    h.toi = 1;
  }

  // Age out before broadphase so dead handles don't generate contacts.
  for (let i = 0; i < _handles.length; i++) {
    const h = _handles[i];
    if (h.group === "projectile") {
      const p = h.ref as Projectile;
      p.ttl -= dt;
      if (p.ttl <= 0) {
        h.dead = true;
      } else if (p.tempK > AMBIENT_K) {
        p.tempK = radiativeCool(p.tempK, h.heatCap, projectileRadiateCoefW(p), dt);
      }
    } else if (h.group === "particle") {
      if (coolSpark(h.ref as Spark, dt)) h.dead = true;
    }
  }

  _grid.clear();
  for (let i = 0; i < _handles.length; i++) {
    const h = _handles[i];
    if (!h.dead) _grid.insert(h);
  }
  _grid.collectPairs(_pairsSeen, _pairsA, _pairsB);

  _contacts.length = 0;
  for (let i = 0; i < _pairsA.length; i++) {
    const a = _pairsA[i];
    const b = _pairsB[i];
    if (a.dead || b.dead) continue;
    const c = narrowPhase(a, b);
    if (c) _contacts.push(c);
  }

  for (let i = 0; i < _contacts.length; i++) {
    const c = _contacts[i];
    if (c.a.dead || c.b.dead) continue;
    const e = contactRestitution(c);
    const eDiss = resolveContact(c, e);
    dispatchOutcome(c, eDiss, world, dirty);
  }

  // CCD rewound to TOI; replay (1−toi) at post-impulse velocity.
  for (let i = 0; i < _handles.length; i++) {
    const h = _handles[i];
    if (h.dead || h.toi >= 1) continue;
    const frac = 1 - h.toi;
    h.pos.x += h.vel.x * frac * dt;
    h.pos.y += h.vel.y * frac * dt;
    if (h.kind === "rigid") h.rot += h.angVel * frac * dt;
  }

  let anyDeadSparks = false;
  for (let i = 0; i < _handles.length; i++) {
    const h = _handles[i];
    writeBack(h);
    if (h.dead) {
      if (h.group === "particle") anyDeadSparks = true;
      else removeFromWorld(world, h);
    }
  }

  if (anyDeadSparks) {
    const sparks = world.sparks;
    let write = 0;
    // Handles enumerate world.sparks in order; walking both = O(n) no Set.
    let hi = 0;
    for (let i = 0; i < sparks.length; i++) {
      while (hi < _handles.length && _handles[hi].group !== "particle") hi++;
      let dead = false;
      if (hi < _handles.length && _handles[hi].id === sparks[i].id) {
        dead = _handles[hi].dead;
        hi++;
      }
      if (!dead) {
        if (write !== i) sparks[write] = sparks[i];
        write++;
      }
    }
    sparks.length = write;
  }

  // Cap by dropping oldest.
  if (world.sparks.length > MAX_SPARKS) {
    const sparks = world.sparks;
    const drop = sparks.length - MAX_SPARKS;
    for (let i = 0; i < MAX_SPARKS; i++) sparks[i] = sparks[i + drop];
    sparks.length = MAX_SPARKS;
  }
}

function collectShipHandles(world: World, vp?: SimViewport): void {
  for (const ship of world.ships.values()) {
    if (vp && isFar(ship.body.pos.x, ship.body.pos.y, vp.focusX, vp.focusY)) continue;
    const cell = shipCellSize(ship);
    const h = acquireHandle();
    let hasSubs = false;
    for (const m of ship.modules) {
      if (m.hp <= 0 || m.def.noCollision) continue;
      const def = m.def;
      const eff = effectiveCells(def, m.facing);
      const cx = (m.gx + eff.w / 2) * cell - ship.com.x;
      const cy = (m.gy + eff.h / 2) * cell - ship.com.y;
      const facingRot = jitterAngle(m.facing * (Math.PI / 2));
      const shape = getCollisionShape(def.sprite);
      if (shape) {
        const cr = Math.cos(facingRot);
        const sr = Math.sin(facingRot);
        for (const r of shape) {
          const ox = (r.cx * cr - r.cy * sr) * cell;
          const oy = (r.cx * sr + r.cy * cr) * cell;
          const s = acquireSub();
          s.hw = r.hw * cell;
          s.hh = r.hh * cell;
          s.cx = cx + ox;
          s.cy = cy + oy;
          s.rot = facingRot;
          s.alive = true;
          s.ref = m;
          h.subs.push(s);
          hasSubs = true;
        }
      } else {
        const s = acquireSub();
        s.hw = eff.w * cell * 0.5;
        s.hh = eff.h * cell * 0.5;
        s.cx = cx;
        s.cy = cy;
        s.rot = facingRot;
        s.alive = true;
        s.ref = m;
        h.subs.push(s);
        hasSubs = true;
      }
    }
    if (!hasSubs) {
      releaseHandle(h);
      continue;
    }
    // Lazy SDF; invalidated by damageModule + recomputeShipMass.
    if (!ship.collider) ship.collider = buildShipCollider(ship);
    h.id = ship.id;
    h.kind = "rigid";
    h.group = ship.isAsteroid ? "asteroid" : "ship";
    h.ref = ship;
    h.pos = ship.body.pos;
    h.prevPos.x = ship.body.pos.x;
    h.prevPos.y = ship.body.pos.y;
    h.vel = ship.body.vel;
    h.rot = ship.body.rot;
    h.prevRot = ship.body.rot;
    h.angVel = ship.body.angVel;
    h.mass = ship.body.mass;
    h.inertia = ship.body.inertia;
    h.restitution = SHIP_RESTITUTION;
    h.toughness = SHIP_TOUGHNESS;
    h.tempK = 0;
    h.heatCap = 0;
    h.pointRadius = 0;
    h.radius = shipExtent(ship);
    h.owner = -1;
    h.connected = collectConnectedShipIds(ship, world);
    h.noSelfContact = false;
    h.dead = false;
    _handles.push(h);
  }
}

/** Per-ship Set: sharing would alias contact filters. */
function collectConnectedShipIds(ship: Ship, world: World): ReadonlySet<number> | null {
  let ids: Set<number> | null = null;
  const add = (id: number): void => {
    if (!world.ships.has(id)) return;
    if (!ids) ids = new Set<number>();
    ids.add(id);
  };
  if (ship.boardedByShipId != null) add(ship.boardedByShipId);
  if (ship.tradeWithShipId != null) add(ship.tradeWithShipId);
  for (const m of ship.modules) {
    if (m.hp <= 0 || !m.docking) continue;
    if (m.docking.peerShipId != null) add(m.docking.peerShipId);
  }
  return ids;
}

function collectItemHandles(world: World, vp?: SimViewport): void {
  for (const it of world.items.values()) {
    if (vp && isFar(it.pos.x, it.pos.y, vp.focusX, vp.focusY)) continue;
    const def = getItemDef(it.defId);
    const r = def.sizeM * 0.5;
    const m = Math.max(0.01, def.stats.mass);
    const inertia = 0.5 * m * r * r;
    const h = acquireHandle();
    h.id = it.id;
    h.kind = "rigid";
    h.group = "item";
    h.ref = it;
    h.pos = it.pos;
    h.prevPos.x = it.pos.x;
    h.prevPos.y = it.pos.y;
    h.vel = it.vel;
    h.rot = it.rot;
    h.prevRot = it.rot;
    h.angVel = it.angVel;
    h.mass = m;
    h.inertia = inertia;
    h.restitution = ITEM_RESTITUTION;
    h.toughness = ITEM_TOUGHNESS;
    h.tempK = it.tempK;
    h.heatCap = m * def.stats.thermalInertia;
    h.pointRadius = r;
    h.radius = r;
    h.owner = -1;
    h.connected = null;
    h.noSelfContact = false;
    h.dead = false;
    _handles.push(h);
  }
}

function collectProjectileHandles(world: World): void {
  for (const p of world.projectiles.values()) {
    const r = p.size * 0.5;
    const m = Math.max(0.0001, p.mass);
    const h = acquireHandle();
    h.id = p.id;
    h.kind = "point";
    h.group = "projectile";
    h.ref = p;
    h.pos = p.pos;
    h.prevPos.x = p.pos.x;
    h.prevPos.y = p.pos.y;
    h.vel = p.vel;
    h.rot = 0;
    h.prevRot = 0;
    h.angVel = 0;
    h.mass = m;
    h.inertia = 0;
    h.restitution = PROJ_RESTITUTION;
    h.toughness = PROJ_TOUGHNESS;
    h.tempK = 0;
    h.heatCap = m * p.specificHeatJPerKgK;
    h.pointRadius = r;
    h.radius = r;
    h.owner = p.ownerId;
    h.connected = null;
    h.noSelfContact = false;
    h.dead = false;
    _handles.push(h);
  }
}

function collectSparkHandles(world: World): void {
  for (let i = 0; i < world.sparks.length; i++) {
    const sp = world.sparks[i];
    const h = acquireHandle();
    h.id = sp.id;
    h.kind = "particle";
    h.group = "particle";
    h.ref = sp;
    h.pos = sp.pos;
    h.prevPos.x = sp.pos.x;
    h.prevPos.y = sp.pos.y;
    h.vel = sp.vel;
    h.rot = 0;
    h.prevRot = 0;
    h.angVel = 0;
    h.mass = sp.mass;
    h.inertia = 0;
    h.restitution = SPARK_RESTITUTION;
    h.toughness = SPARK_TOUGHNESS;
    h.tempK = 0;
    h.heatCap = sparkHeatCapJPerK(sp);
    h.pointRadius = SPARK_RADIUS_M;
    h.radius = SPARK_RADIUS_M;
    h.owner = -1;
    h.connected = null;
    h.noSelfContact = true;
    h.dead = false;
    _handles.push(h);
  }
}

function writeBack(h: BodyHandle): void {
  if (h.group === "ship" || h.group === "asteroid") {
    const ship = h.ref as Ship;
    ship.body.rot = h.rot;
    ship.body.angVel = h.angVel;
  } else if (h.group === "item") {
    const it = h.ref as SpaceItem;
    it.rot = h.rot;
    it.angVel = h.angVel;
    it.tempK = h.tempK;
  }
}

function removeFromWorld(world: World, h: BodyHandle): void {
  if (h.group === "projectile") world.projectiles.delete(h.id);
  else if (h.group === "item") world.items.delete(h.id);
}

function dispatchOutcome(
  c: Contact, eDiss: number,
  world: World, dirty: Set<Ship>,
): void {
  const a = c.a, b = c.b;
  if (groupRank(a.group) > groupRank(b.group)) {
    dispatchOrdered(b, a, c, eDiss, true, world, dirty);
  } else {
    dispatchOrdered(a, b, c, eDiss, false, world, dirty);
  }
}

function groupRank(g: BodyHandle["group"]): number {
  switch (g) {
    case "ship": return 0;
    case "asteroid": return 1;
    case "item": return 2;
    case "projectile": return 3;
    case "particle": return 4;
  }
}

function dispatchOrdered(
  a: BodyHandle, b: BodyHandle,
  c: Contact, eDiss: number, swapped: boolean,
  world: World, dirty: Set<Ship>,
): void {
  const ag = a.group, bg = b.group;
  if (ag === "ship" || ag === "asteroid") {
    if (bg === "ship" || bg === "asteroid") onShipShip(a, b, c, eDiss, world, dirty);
    else if (bg === "item") onShipItem(a, b, c, eDiss, swapped, world, dirty);
    else if (bg === "projectile") onShipProjectile(a, b, c, eDiss, swapped, world, dirty);
    return;
  }
  if (ag === "item") {
    if (bg === "item") onItemItem(a, b, eDiss);
    else if (bg === "projectile") onItemProjectile(a, b, eDiss, swapped);
    return;
  }
  if (ag === "projectile") {
    if (bg === "projectile") onProjectileProjectile(a, b);
  }
}

function onShipShip(
  a: BodyHandle, b: BodyHandle,
  c: Contact, eDiss: number,
  world: World, dirty: Set<Ship>,
): void {
  const shipA = a.ref as Ship;
  const shipB = b.ref as Ship;
  const modA = c.subA ? (c.subA.ref as ShipModule) : null;
  const modB = c.subB ? (c.subB.ref as ShipModule) : null;
  if (!modA || !modB) return;

  if (eDiss <= 0) return;
  emitContactSparks(world, shipA, modA, shipB, modB, c, eDiss);

  const heatJ = collisionHeatJ(eDiss);
  damageModule(world, shipA, modA, kineticHP(a, eDiss), 0.01, heatShareJ(heatJ, 2), dirty, "collision");
  damageModule(world, shipB, modB, kineticHP(b, eDiss), 0.01, heatShareJ(heatJ, 2), dirty, "collision");
}

function onShipItem(
  ship_h: BodyHandle, item_h: BodyHandle,
  c: Contact, eDiss: number,
  _swapped: boolean,
  world: World, dirty: Set<Ship>,
): void {
  const ship = ship_h.ref as Ship;
  const item = item_h.ref as SpaceItem;
  const m = c.subA ? (c.subA.ref as ShipModule) : null;
  if (!m) return;

  const def = m.def;
  if (!ship.isAsteroid && def.collector) {
    const itemDef = getItemDef(item.defId);
    if (itemDef.resource) {
      const net = getNetworks(ship).networkOf.get(m);
      if (net) {
        const space = networkCargoCapacity(net) - networkCargoMass(net);
        if (space > 0) {
          const want = Math.min(itemDef.stats.mass, space);
          const got = addCargoToNetwork(net, itemDef.resource, want, item.quality);
          if (got > 0) {
            item_h.dead = true;
            return;
          }
        }
      }
    }
  }

  if (eDiss > 0) {
    const heatJ = collisionHeatJ(eDiss);
    damageModule(world, ship, m, kineticHP(ship_h, eDiss), 0.01, heatShareJ(heatJ, 2), dirty, "collision");
    if (item_h.heatCap > 0) item_h.tempK += heatShareJ(heatJ, 2) / item_h.heatCap;
    const dmgItem = kineticHP(item_h, eDiss);
    if (dmgItem > 0) {
      item.hp -= dmgItem;
      if (item.hp <= 0) item_h.dead = true;
    }
  }
}

function plasticYieldScale(c: Contact): number {
  const mr = (c.a.mass * c.b.mass) / (c.a.mass + c.b.mass);
  const ke = 0.5 * mr * c.vClosing * c.vClosing;
  const t = (ke - E_YIELD_J) / (E_FULL_PLASTIC_J - E_YIELD_J);
  return t <= 0 ? 1 : t >= 1 ? 0 : 1 - t;
}

function contactRestitution(c: Contact): number {
  const aIsProj = c.a.group === "projectile";
  const bIsProj = c.b.group === "projectile";
  const yieldScale = plasticYieldScale(c);
  if (!aIsProj && !bIsProj) {
    const baseE = (c.a.restitution + c.b.restitution) * 0.5;
    return baseE * yieldScale;
  }

  const proj_h = aIsProj ? c.a : c.b;
  const other = aIsProj ? c.b : c.a;
  if (other.group !== "ship" && other.group !== "asteroid") return 0;

  const p = proj_h.ref as Projectile;
  if (p.restitution <= 0) return 0;

  const speed = Math.hypot(proj_h.vel.x, proj_h.vel.y);
  if (speed < 1e-6) return 0;
  const cosTheta = Math.abs(proj_h.vel.x * c.nx + proj_h.vel.y * c.ny) / speed;

  let hardness = 0;
  const sub = aIsProj ? c.subB : c.subA;
  if (sub) hardness = (sub.ref as ShipModule).def.stats.hardness;
  const halfAngle = jitterAngle(SOFT_ABSORB_HALF_ANGLE_RAD * (1 - hardness));
  if (cosTheta >= Math.cos(halfAngle)) return 0;
  return p.restitution * yieldScale;
}

function onShipProjectile(
  ship_h: BodyHandle, proj_h: BodyHandle,
  c: Contact, eDiss: number, _swapped: boolean,
  world: World, dirty: Set<Ship>,
): void {
  const ship = ship_h.ref as Ship;
  const p = proj_h.ref as Projectile;
  const m = c.subA ? (c.subA.ref as ShipModule) : null;
  if (!m) return;

  const face = worldNormalToShipFace(ship, c.nx, c.ny);
  emitSparks(world, ship, m, c.cx, c.cy, face.dx, face.dy, eDiss);

  const postNormalSpeed = Math.abs(p.vel.x * c.nx + p.vel.y * c.ny);
  const postSpeed = Math.hypot(p.vel.x, p.vel.y);
  const ricochets = postNormalSpeed > 1 && postSpeed >= ABSORB_SPEED_THRESHOLD_M_S;

  const jitter = ricochets ? 1 - Math.random() * RICOCHET_HEAT_JITTER : 1;
  // Heat tracks damage: an embed dissipates more energy than a glancing
  // ricochet, so it deposits the larger heatOnImpactJ while a ricochet deposits
  // the smaller heatOnRicochetJ (jittered).
  const moduleHeat = ricochets ? p.heatOnRicochetJ * jitter : p.heatOnImpactJ;
  damageModule(world, ship, m, kineticHP(proj_h, eDiss), p.qualityMod,
    moduleHeat, dirty, "bullet");
  if (ricochets) {
    p.tempK = RICOCHET_GLOW_K * jitter;
    // Ricochets can hit anything (including firer).
    p.ownerId = -1;
    return;
  }

  if (p.blastRadiusM > 0) {
    damageModule(world, ship, m, p.damage, p.qualityMod, 0, dirty, "bullet");
    applyBlastDamage(world, ship, c.cx, c.cy, p.blastRadiusM, p.damage, p.qualityMod, p.heatOnImpactJ, m, dirty);
  }
  proj_h.dead = true;
}

function onItemItem(a: BodyHandle, b: BodyHandle, eDiss: number): void {
  if (eDiss <= 0) return;
  const dmgA = kineticHP(a, eDiss);
  const dmgB = kineticHP(b, eDiss);
  const itA = a.ref as SpaceItem;
  const itB = b.ref as SpaceItem;
  itA.hp -= dmgA;
  itB.hp -= dmgB;
  if (itA.hp <= 0) a.dead = true;
  if (itB.hp <= 0) b.dead = true;
  const heatJ = collisionHeatJ(eDiss);
  if (a.heatCap > 0) a.tempK += heatShareJ(heatJ, 2) / a.heatCap;
  if (b.heatCap > 0) b.tempK += heatShareJ(heatJ, 2) / b.heatCap;
}

function onItemProjectile(
  item_h: BodyHandle, proj_h: BodyHandle,
  eDiss: number, _swapped: boolean,
): void {
  const item = item_h.ref as SpaceItem;
  const p = proj_h.ref as Projectile;
  item.hp -= kineticHP(proj_h, eDiss);
  if (item.hp <= 0) item_h.dead = true;
  if (item_h.heatCap > 0) {
    // Items absorb the round (embed), so they take the embed heat.
    item_h.tempK += p.heatOnImpactJ / item_h.heatCap;
  }
  proj_h.dead = true;
}

function onProjectileProjectile(a: BodyHandle, b: BodyHandle): void {
  a.dead = true;
  b.dead = true;
}

function emitContactSparks(
  world: World,
  shipA: Ship, mA: ShipModule,
  shipB: Ship, mB: ShipModule,
  c: Contact,
  eDiss: number,
): void {
  const ccx = c.cx, ccy = c.cy;
  const rAx = ccx - shipA.body.pos.x;
  const rAy = ccy - shipA.body.pos.y;
  const aVx = shipA.body.vel.x - shipA.body.angVel * rAy;
  const aVy = shipA.body.vel.y + shipA.body.angVel * rAx;
  const rBx = ccx - shipB.body.pos.x;
  const rBy = ccy - shipB.body.pos.y;
  const bVx = shipB.body.vel.x - shipB.body.angVel * rBy;
  const bVy = shipB.body.vel.y + shipB.body.angVel * rBx;
  const fA = worldNormalToShipFace(shipA, c.nx, c.ny);
  const fB = worldNormalToShipFace(shipB, -c.nx, -c.ny);
  const eSide = eDiss * 0.5;
  emitSparks(world, shipA, mA, ccx, ccy, fA.dx, fA.dy, eSide, aVx, aVy);
  emitSparks(world, shipB, mB, ccx, ccy, fB.dx, fB.dy, eSide, bVx, bVy);
}
